/**
 * PresentDetailPresenter
 * --
 */
import React from 'react';
import MessageAlert, { STORE_URL, stringToMoneyFormat } from 'utils';
import {
  BottomLayout,
  Col,
  Content,
  Image,
  ModalLayout,
  Row,
  Text,
} from 'components';
import { Button, Spin, Input, Select, Flex } from 'antd';
import dayjs from 'dayjs';
import { DownloadOutlined } from '@ant-design/icons';
import { openAddressModal } from 'utils/DaumAPIManager';
import Formatter from 'utils/Formatter';
import { ReceiveList } from './components';
import { GiftCard } from '../Present/components';

const PRESENT_TYPE = {
  AMOUNT: '금액',
  COUNT: '수량',
};

const PRESENT_UNIT = {
  AMOUNT: '원',
  COUNT: '개',
};

const PRESENT_STATUS = {
  USED: '사용됨',
  EXPIRED: '만료됨',
  USEABLE: '사용가능',
  CANCELLED: '취소됨',
};

const DELIVERY_REQUEST = [
  {
    label: '문 앞에 놔주세요',
    value: '문 앞에 놔주세요',
  },
  {
    label: '경비실에 맡겨주세요',
    value: '경비실에 맡겨주세요',
  },
  {
    label: '택배함에 넣어주세요',
    value: '택배함에 넣어주세요',
  },
  {
    label: '배송 전에 연락 주세요',
    value: '배송 전에 연락 주세요',
  },
  {
    label: '직접입력',
    value: null,
  },
];

const PresentDetailPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    data,
    isLoading,
    onDownload,
    inputValue,
    onChange,
    onSubmit,
    selected,
    onSelect,
    card,
    onExtension,
    userName,
    isMobile,
  } = props;

  /* ===== STATE ===== */
  const [isOpened, setIsOpened] = React.useState(false);
  const [isOpenAmount, setIsOpenAmount] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState('');
  const [cardOpened, setCardOpened] = React.useState(false);

  /* ===== VARIABLE ===== */
  const styles = {
    container: {
      border: '1px solid #DDDDDD',
      padding: 24,
      borderRadius: 10,
    },
    flexRow: {
      display: 'flex',
    },
    flexCol: {
      display: 'flex',
      flexDirection: 'column',
    },

    title: {
      fontSize: 16,
      fontWeight: 700,
    },

    infoItem: {
      justifyContent: 'space-between',
      borderBottom: '1px solid #EEEEEE',
      padding: '4px 0px',
    },

    submit: {
      width: '100%',
    },
    button: {
      fontSize: 11,
      borderColor: '#DDDDDD',
    },

    tab: {
      display: 'flex',
    },
    tabItem: {
      width: '50%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
      cursor: 'pointer',
      background: '#F4F4F4',
      border: '1px solid #DDDDDD',
    },
    tabItemActive: {
      borderBottom: 'none',
      background: '#FFFFFF',
    },

    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: 5,
    },
  };

  /* ===== FUNCTION ===== */
  /**
   * 다음 주소 API
   * --
   */
  const handleAddressModal = () => {
    openAddressModal((res) => {
      const { address, zonecode } = res;

      onChange('recipient_address', address);
      onChange('recipient_postcode', zonecode);
    });
  };

  /* ===== HOOKS ===== */

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content
          maxWidth={'100%'}
          padding={0}
          backgroundColor="#70737C14"
          minHeight={'100vh'}
        >
          <Row>
            <Col x={24}>
              <Content
                maxWidth={500}
                height={'100%'}
                padding={0}
                style={{ paddingBottom: 50 }}
              >
                <Row gutter={[0, 12]}>
                  <Col x={24}>
                    <Content
                      maxWidth={'100%'}
                      padding={0}
                      style={{ position: 'relative', top: 0, left: 0 }}
                    >
                      <Image
                        src={`${STORE_URL}/${data?.present_image_url}`}
                        alt={'상품 이미지'}
                      />
                      {data?.status !== 'USEABLE' ||
                      data?.payment_state === 'CANCELLED' ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backdropFilter: 'blur(3px)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Text size={24} weight={700}>
                            {data?.payment_state === 'CANCELLED'
                              ? PRESENT_STATUS[data?.payment_state]
                              : PRESENT_STATUS[data?.status]}
                          </Text>
                        </div>
                      ) : null}
                    </Content>
                  </Col>

                  <Col x={24}>
                    <Content maxWidth={'100%'}>
                      <div style={{ ...styles.container }}>
                        {data?.payment_state !== 'CANCELLED' && (
                          <div
                            style={{
                              ...styles.flexRow,
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 10,
                            }}
                          >
                            <Text style={{ ...styles.title }}>선물 정보</Text>
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              style={{ ...styles.button }}
                              onClick={() =>
                                onDownload(
                                  data?.present_image_url,
                                  data?.barcode,
                                )
                              }
                            >
                              저장하기
                            </Button>
                          </div>
                        )}

                        <div style={{ ...styles.flexCol, gap: 8 }}>
                          <div
                            style={{
                              ...styles.flexRow,
                              justifyContent: 'space-between',
                              background: '#EEEEEE',
                              padding: 10,
                            }}
                          >
                            <Text size={14}>
                              사용 가능 {PRESENT_TYPE[data?.present_type]}
                            </Text>

                            <Text weight={700} color={'#900020'}>
                              {stringToMoneyFormat(
                                data?.received_present - data?.used_present,
                                PRESENT_UNIT[data?.present_type],
                              )}
                            </Text>
                          </div>

                          <div
                            style={{
                              ...styles.flexRow,
                              ...styles.infoItem,
                            }}
                          >
                            <Text size={14}>
                              {data?.product?.type !== 'AMOUNT'
                                ? '유효기간'
                                : '선물 등록유효기간'}
                            </Text>

                            <Text size={14}>
                              {dayjs
                                .unix(data?.expired_date)
                                .format('YYYY년 MM월 DD일')}
                            </Text>
                          </div>
                          <div
                            style={{
                              ...styles.flexRow,
                              ...styles.infoItem,
                            }}
                          >
                            <Text size={14}>선물 주문일</Text>

                            <Text size={14}>
                              {dayjs
                                .unix(data?.created_at)
                                .format('YYYY년 MM월 DD일')}
                            </Text>
                          </div>
                          <div
                            style={{
                              ...styles.flexRow,
                              ...styles.infoItem,
                            }}
                          >
                            <Text size={14}>쿠폰 상태</Text>

                            <Text size={14}>
                              {PRESENT_STATUS[data?.status]}
                            </Text>
                          </div>
                          {data?.type === 'PRODUCT' ? (
                            <React.Fragment>
                              <div
                                style={{
                                  ...styles.flexRow,
                                  ...styles.infoItem,
                                }}
                              >
                                <Text size={14}>택배비</Text>

                                <div>
                                  <Text size={12} color={'#666666'}>
                                    도서산간지역 배송비 별도
                                  </Text>
                                </div>
                              </div>
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <div
                                style={{
                                  ...styles.flexRow,
                                  ...styles.infoItem,
                                }}
                              >
                                <Flex vertical>
                                  <Text size={14}>연장가능 횟수</Text>
                                  <Text size={10} color={'#900020'}>
                                    * 쿠폰의 유효기간 만료 7일전부터 연장
                                    가능합니다.
                                  </Text>
                                </Flex>

                                <Flex gap={8} align="center">
                                  {data?.extendable_count > 0 ? (
                                    <Button
                                      type="text"
                                      size="small"
                                      style={{
                                        padding: 0,
                                        color:
                                          dayjs
                                            .unix(data?.expired_date)
                                            .diff(dayjs(), 'day') <= 7
                                            ? '#900020'
                                            : '#BDBDBD',
                                      }}
                                      disabled={
                                        dayjs
                                          .unix(data?.expired_date)
                                          .diff(dayjs(), 'day') <= 7
                                          ? false
                                          : true
                                      }
                                      onClick={() =>
                                        onExtension(
                                          data?.present_id,
                                          data?.expired_date,
                                          data?.product?.present_information
                                            ?.present_expired_date,
                                          data?.extendable_count,
                                        )
                                      }
                                    >
                                      연장하기
                                    </Button>
                                  ) : null}

                                  <Text size={14}>
                                    {data?.extendable_count} 회
                                  </Text>
                                </Flex>
                              </div>
                            </React.Fragment>
                          )}

                          <div
                            style={{
                              ...styles.flexRow,
                              justifyContent: 'center',
                            }}
                          >
                            <Button onClick={() => setCardOpened(!cardOpened)}>
                              메세지 카드 보기
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Content>
                  </Col>
                </Row>
              </Content>
            </Col>
          </Row>
        </Content>
      </Spin>

      {/* 배송지 등록 */}
      <ModalLayout
        type="drawer"
        height={'100%'}
        open={isOpened}
        closable
        placement="bottom"
        onCancel={() => setIsOpened(!isOpened)}
        title={'배송지등록'}
        styles={{
          body: {
            padding: 0,
            height: '100%',
          },
        }}
      >
        <Spin spinning={isLoading} fullscreen />
        <Content
          maxWidth={'100%'}
          padding={0}
          backgroundColor="#70737C14"
          height={'100%'}
        >
          <Row style={{ height: '100%' }}>
            <Col x={24} style={{ height: '100%' }}>
              <Content
                maxWidth={500}
                padding={0}
                height={'100%'}
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                {data?.shipping_type === 'DELIVERY' ? (
                  <Content
                    maxWidth={'100%'}
                    padding={24}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 20,
                      position: 'relative',
                      top: 0,
                      left: 0,
                    }}
                  >
                    <div style={{ ...styles.form }}>
                      <Text size={14}>이름</Text>
                      <Input
                        size="large"
                        placeholder="받는 분의 성함을 입력해주세요"
                        value={inputValue?.recipient_name}
                        onChange={(e) =>
                          onChange('recipient_name', e.target.value)
                        }
                      />
                    </div>

                    <div style={{ ...styles.form }}>
                      <Text size={14}>휴대폰번호</Text>
                      <Input
                        size="large"
                        placeholder="받는 분의 휴대폰번호를 입력해주세요"
                        value={inputValue?.recipient_phone}
                        onChange={(e) =>
                          onChange(
                            'recipient_phone',
                            Formatter.hyphen(e.target.value),
                          )
                        }
                      />
                    </div>

                    <div style={{ ...styles.form }}>
                      <Text size={14}>주소</Text>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'stretch',
                          gap: 10,
                        }}
                      >
                        <Input
                          size="large"
                          placeholder="우편번호"
                          value={inputValue?.recipient_postcode}
                          onChange={(e) =>
                            onChange(
                              'recipient_postcode',
                              Formatter.hyphen(e.target.value),
                            )
                          }
                          readOnly
                        />
                        <button
                          style={{
                            border: '1px solid #111111',
                            background: '#FFFFFF',
                            cursor: 'pointer',
                            fontSize: 14,
                            padding: '2px 10px',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => handleAddressModal()}
                        >
                          주소 찾기
                        </button>
                      </div>
                      <Input
                        size="large"
                        placeholder="주소"
                        value={inputValue?.recipient_address}
                        onChange={(e) =>
                          onChange('recipient_address', e.target.value)
                        }
                        readOnly
                      />
                      <Input
                        size="large"
                        placeholder="주소 상세"
                        value={inputValue?.recipient_address_detail}
                        onChange={(e) =>
                          onChange('recipient_address_detail', e.target.value)
                        }
                      />
                    </div>

                    <div style={{ ...styles.form }}>
                      <Text size={14}>배송 요청사항 (선택)</Text>
                      <Select
                        size="large"
                        options={DELIVERY_REQUEST}
                        placeholder="배송 요청사항을 선택해주세요"
                        value={selectedValue}
                        onChange={(e) => {
                          onChange('request', e);
                          setSelectedValue(e);
                        }}
                      />
                      {selectedValue === null ? (
                        <Input.TextArea
                          style={{ height: 60 }}
                          maxLength={50}
                          value={inputValue?.request}
                          onChange={(e) => onChange('request', e.target.value)}
                          showCount
                        />
                      ) : null}
                    </div>

                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                      }}
                    >
                      <Button
                        type="primary"
                        size="large"
                        style={{ width: '100%' }}
                        onClick={() => onSubmit()}
                      >
                        {data?.shipping_type === 'DELIVERY' &&
                        data?.final_delivery_price
                          ? `배송비 ${stringToMoneyFormat(
                              data?.final_delivery_price,
                              '원',
                            )} 결제하기`
                          : '주문하기'}
                      </Button>
                    </div>
                  </Content>
                ) : (
                  <Content
                    maxWidth={'100%'}
                    padding={24}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 20,
                      position: 'relative',
                      top: 0,
                      left: 0,
                    }}
                  >
                    <ReceiveList
                      data={data?.product}
                      selected={selected}
                      onSelect={onSelect}
                    />

                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                      }}
                    >
                      <Button
                        type="primary"
                        size="large"
                        style={{ width: '100%' }}
                        onClick={() => onSubmit()}
                      >
                        주문하기
                      </Button>
                    </div>
                  </Content>
                )}
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
      <ModalLayout
        type={'modal'}
        open={isOpenAmount}
        onCancel={() => setIsOpenAmount(false)}
        centered
        closable
        title={
          !userName
            ? '앱 설치 및 회원가입 후 선물함 또는 선물받은 메시지에서 상품권의 [사용하기] 버튼을 눌러주세요.'
            : '제이엠페이로 자동 전환 적립됩니다.'
        }
        width={300}
        footer={
          <Content maxWidth={'100%'} padding={0}>
            <Row>
              <Col x={24}>
                <Button onClick={() => setIsOpenAmount(false)}>취소</Button>
                &nbsp;
                <Button
                  type="primary"
                  onClick={() => {
                    if (!userName) {
                      MessageAlert.warning('회원가입 이후 사용할 수 있습니다!');
                      setIsOpenAmount(false);
                      if (isMobile) {
                        window.open(
                          'https://s3.ap-northeast-2.amazonaws.com/jmcoffeemall.com/Redirect.html',
                          '_blank',
                        );
                      }
                    } else if (onSubmit) {
                      onSubmit();

                      setIsOpenAmount(false);
                    }
                  }}
                >
                  {!userName ? '확인' : '사용하기'}
                </Button>
              </Col>
            </Row>
          </Content>
        }
      />
      {/* 메세지 카드 */}
      <ModalLayout
        type="drawer"
        height={'100%'}
        open={cardOpened}
        closable
        placement="bottom"
        onCancel={() => setCardOpened(!cardOpened)}
        title={'메세지 카드'}
        styles={{
          body: {
            padding: 0,
            height: '100%',
          },
        }}
      >
        <Content
          maxWidth={'100%'}
          padding={0}
          backgroundColor="#70737C14"
          height={'100%'}
        >
          <Row style={{ height: '100%' }}>
            <Col x={24} style={{ height: '100%' }}>
              <Content
                maxWidth={500}
                padding={24}
                height={'100%'}
                style={{ display: 'flex', flexDirection: 'column' }}
                backgroundColor="none"
              >
                <Flex vertical justify="center" align="center">
                  <GiftCard url={card} style={{ width: '100%' }} />

                  <Flex
                    vertical
                    flex={1}
                    style={{
                      width: '100%',
                      padding: 5,
                      background: '#FFFFFF',
                      borderRadius: 4,
                    }}
                  >
                    {data?.message ? (
                      <div
                        style={{
                          padding: 10,
                          borderBottom: '1px dashed #F5F5F5',
                        }}
                      >
                        {data?.message}
                      </div>
                    ) : null}

                    <Flex
                      gap={4}
                      justify="flex-start"
                      style={{ padding: '18px 10px' }}
                    >
                      <Image
                        src={data?.product?.thumbnail}
                        width={40}
                        height={40}
                      />
                      <Flex vertical justify="space-between">
                        <Text size={12} color={'#BDBDBD'}>
                          {data?.product?.shop_information_name}
                        </Text>
                        <Text size={14}>
                          {
                            data?.product?.[
                              `${data?.type?.toLocaleLowerCase()}_name`
                            ]
                          }
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </Content>
            </Col>
          </Row>
        </Content>
      </ModalLayout>
      {data?.payment_state !== 'CANCELLED' ? (
        data?.type !== 'PRODUCT' &&
        data?.status === 'USEABLE' &&
        data?.product?.type !== 'AMOUNT' ? (
          <BottomLayout shadow>
            <Button
              type="primary"
              size="large"
              style={{ ...styles.submit }}
              onClick={() => setIsOpened(!isOpened)}
            >
              배송지 등록하기
            </Button>
          </BottomLayout>
        ) : (
          <BottomLayout shadow>
            <Button
              type="primary"
              size="large"
              style={{ ...styles.submit }}
              onClick={() => setIsOpenAmount(!isOpenAmount)}
            >
              {userName ? '사용하기' : '앱 설치'}
            </Button>
          </BottomLayout>
        )
      ) : null}
    </React.Fragment>
  );
};

export default PresentDetailPresenter;
